import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { cn } from '../../lib/utils';
import { DollarSign, CheckCircle2, Clock, AlertTriangle, TrendingUp, Users, CreditCard, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import CrossAppDialog from '../../components/shared/CrossAppDialog';
import { useAppMode } from '../../contexts/AppModeContext';

const BILLING_SUMMARY = { copaysCollected: 4680, copaysPending: 1250, outstanding: 8920, dailyTarget: 6000 };

const TODAYS_COPAYS = [
  { id: 1, patient: 'John Smith', amount: 30, status: 'collected', time: '9:15 AM', method: 'Card' },
  { id: 2, patient: 'Maria Garcia', amount: 30, status: 'collected', time: '9:45 AM', method: 'Cash' },
  { id: 3, patient: 'Robert Johnson', amount: 30, status: 'pending', time: '10:00 AM', method: null },
  { id: 4, patient: 'Emily Davis', amount: 45, status: 'pending', time: '10:30 AM', method: null },
  { id: 5, patient: 'James Taylor', amount: 30, status: 'waived', time: '11:00 AM', method: null },
  { id: 6, patient: 'Sarah Williams', amount: 30, status: 'collected', time: '1:00 PM', method: 'Card' },
];

const EchoBilling = () => {
  const { isDemo } = useAppMode();
  const [showRevQ, setShowRevQ] = useState(false);
  const [copays, setCopays] = useState(TODAYS_COPAYS);

  const collectCopay = (id) => {
    setCopays(prev => prev.map(c => c.id === id ? { ...c, status: 'collected', method: 'Card' } : c));
    toast.success('Copay collected');
  };

  return (
    <div className="space-y-6" data-testid="echo-billing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Front Office Billing</h1>
          <p className="text-slate-500 mt-1">Copay collection and daily billing summary</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => setShowRevQ(true)} data-testid="full-billing-btn"><DollarSign className="h-4 w-4" /> Full Billing in revQ <ArrowRight className="h-3 w-3" /></Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Copays Collected', value: `$${BILLING_SUMMARY.copaysCollected.toLocaleString()}`, icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Copays Pending', value: `$${BILLING_SUMMARY.copaysPending.toLocaleString()}`, icon: Clock, color: 'text-amber-600 bg-amber-50' },
          { label: 'Outstanding', value: `$${BILLING_SUMMARY.outstanding.toLocaleString()}`, icon: AlertTriangle, color: 'text-red-600 bg-red-50' },
          { label: 'Daily Target', value: `$${BILLING_SUMMARY.dailyTarget.toLocaleString()}`, icon: TrendingUp, color: 'text-blue-600 bg-blue-50' },
        ].map((s, i) => (
          <Card key={i} className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
            <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center", s.color.split(' ')[1])}><s.icon className={cn("h-4 w-4", s.color.split(' ')[0])} /></div>
            <div><p className="text-xl font-bold">{s.value}</p><p className="text-[10px] text-muted-foreground">{s.label}</p></div>
          </CardContent></Card>
        ))}
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Today's Copay Tracker</CardTitle></CardHeader>
        <Table>
          <TableHeader><TableRow><TableHead>Patient</TableHead><TableHead>Amount</TableHead><TableHead>Time</TableHead><TableHead>Method</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {copays.map(c => (
              <TableRow key={c.id} data-testid={`copay-row-${c.id}`}>
                <TableCell className="font-medium text-sm">{c.patient}</TableCell>
                <TableCell className="text-sm">${c.amount}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.time}</TableCell>
                <TableCell>{c.method ? <Badge variant="outline" className="text-[10px]">{c.method}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                <TableCell><Badge className={cn("text-[10px]", c.status === 'collected' ? 'bg-emerald-100 text-emerald-700' : c.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600')}>{c.status}</Badge></TableCell>
                <TableCell>{c.status === 'pending' && <Button size="sm" className="h-7 text-xs bg-black hover:bg-gray-800" onClick={() => collectCopay(c.id)}>Collect</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DollarSign className="h-5 w-5 text-slate-400" />
            <div><p className="text-sm font-medium text-slate-700">Claims, denials, and AR management</p><p className="text-xs text-muted-foreground">Full revenue cycle management is available in revQ.</p></div>
          </div>
          <Button onClick={() => setShowRevQ(true)} className="bg-black hover:bg-gray-800 gap-2"><DollarSign className="h-4 w-4" /> Open revQ</Button>
        </CardContent>
      </Card>

      <CrossAppDialog open={showRevQ} onOpenChange={setShowRevQ} targetApp="revq" context="Full billing, claims, and revenue cycle management require revQ." />
    </div>
  );
};

export default EchoBilling;
